/**
 * Statut juridique et identité d'une partie (migration 0050) — ce que la console montre et saisit.
 *
 * Les RÈGLES de rendu (désignation de la partie, champs exigés, libellés) viennent du module PUR
 * `supabase/functions/_shared/contrat-variables.ts`, le même que les edge functions : on l'importe, on ne
 * le recopie jamais. La base reste l'autorité qui bloque ; ici on prévient le staff AVANT l'appel
 * (aperçu en direct de la désignation, liste de ce qui manque).
 */
import {
  CHAMPS_REQUIS,
  champsManquants,
  designationPartie,
  FORMES_JURIDIQUES,
  IdentiteIncomplete,
  LIBELLE_CHAMP,
  LIBELLE_FORME,
  LIBELLE_STATUT,
  STATUTS_JURIDIQUES,
  TYPES_PIECE,
  type IdentiteContrat,
  type StatutJuridique,
  type SujetContrat,
} from '../../../../supabase/functions/_shared/contrat-variables.ts'

export {
  CHAMPS_REQUIS,
  FORMES_JURIDIQUES,
  LIBELLE_CHAMP,
  LIBELLE_FORME,
  LIBELLE_STATUT,
  STATUTS_JURIDIQUES,
  TYPES_PIECE,
}
export type { IdentiteContrat, StatutJuridique, SujetContrat }

/** Libellés courts des pièces d'identité (liste déroulante). */
export const LIBELLE_TYPE_PIECE: Record<string, string> = {
  cni: "Carte nationale d'identité",
  passeport: 'Passeport',
  carte_nina: 'Carte NINA',
  carte_consulaire: 'Carte consulaire',
  autre: 'Autre pièce',
}

/** Les statuts proposés à chaque sujet : un livreur n'est jamais une société. */
export function statutsProposes(sujet: SujetContrat): readonly StatutJuridique[] {
  return sujet === 'livreur' ? ['entreprenant', 'personne_physique'] : STATUTS_JURIDIQUES
}

/** Une saisie de formulaire : tout est du texte (les `<input>` n'en connaissent pas d'autre). */
export type IdentiteSaisie = { statut_juridique: StatutJuridique | '' } & Record<string, string>

export const CHAMPS_IDENTITE = [
  'forme_juridique', 'raison_sociale', 'nom_commercial', 'capital_fcfa', 'rccm', 'nif',
  'numero_declaration_entreprenant', 'siege_adresse', 'representant_nom', 'representant_qualite',
  'personne_nom', 'personne_prenoms', 'date_naissance', 'lieu_naissance', 'type_piece', 'numero_piece',
  'adresse_personne',
] as const

export function saisieVide(statut: StatutJuridique | '' = ''): IdentiteSaisie {
  const s: IdentiteSaisie = { statut_juridique: statut }
  for (const c of CHAMPS_IDENTITE) s[c] = ''
  return s
}

/** Ligne de la base (ou `null`) → saisie de formulaire. */
export function saisieDepuis(ligne: Record<string, unknown> | null | undefined): IdentiteSaisie {
  if (!ligne) return saisieVide()
  const s = saisieVide((ligne['statut_juridique'] as StatutJuridique | undefined) ?? '')
  for (const c of CHAMPS_IDENTITE) {
    const v = ligne[c]
    s[c] = v === null || v === undefined ? '' : String(v)
  }
  return s
}

/**
 * Les champs à AFFICHER (dans l'ordre du formulaire) pour un sujet et un statut. Obligatoires (`requis`) ou
 * facultatifs : seuls les champs que le contrat sait utiliser, pour ne pas noyer la personne.
 */
export function champsVisibles(sujet: SujetContrat, statut: StatutJuridique | ''): { cle: string; requis: boolean }[] {
  if (statut === '') return []
  const requis = new Set<string>(CHAMPS_REQUIS[sujet][statut])
  const ordre: string[] =
    statut === 'societe'
      ? ['raison_sociale', 'forme_juridique', 'nom_commercial', 'capital_fcfa', 'rccm', 'nif', 'siege_adresse',
        'representant_nom', 'representant_qualite']
      : statut === 'entreprenant'
        ? ['personne_nom', 'personne_prenoms', 'date_naissance', 'lieu_naissance', 'type_piece', 'numero_piece',
          'adresse_personne', 'numero_declaration_entreprenant', 'nom_commercial', 'nif']
        : ['personne_nom', 'personne_prenoms', 'date_naissance', 'lieu_naissance', 'type_piece', 'numero_piece',
          'adresse_personne']
  // Un livreur n'a pas de nom commercial (son NIF, lui, est facultatif s'il est entreprenant).
  return ordre
    .filter((c) => !(sujet === 'livreur' && c === 'nom_commercial'))
    .map((cle) => ({ cle, requis: requis.has(cle) }))
}

/** La saisie → ce que la RPC `enregistrer_identite_*` reçoit (jsonb) : les champs vides sont omis. */
export function versPayload(sujet: SujetContrat, saisie: IdentiteSaisie): Record<string, string> {
  const out: Record<string, string> = {}
  if (saisie.statut_juridique === '') return out
  out['statut_juridique'] = saisie.statut_juridique
  for (const { cle } of champsVisibles(sujet, saisie.statut_juridique)) {
    const v = (saisie[cle] ?? '').trim()
    if (v !== '') out[cle] = v
  }
  return out
}

/** Champs encore à remplir selon la saisie EN COURS (avant enregistrement) : même règle que la base. */
export function manquantsDeLaSaisie(sujet: SujetContrat, saisie: IdentiteSaisie): string[] {
  return champsManquants(sujet, versPayload(sujet, saisie) as IdentiteContrat)
}

export interface ApercuDesignation {
  /** Le paragraphe tel qu'il figurera dans le contrat, ou `null` s'il manque des champs. */
  texte: string | null
  manquants: string[]
}

/** Aperçu EN DIRECT de la désignation, calculé par le module de rendu des edge functions. */
export function apercuDesignation(sujet: SujetContrat, saisie: IdentiteSaisie): ApercuDesignation {
  const payload = versPayload(sujet, saisie) as IdentiteContrat
  try {
    return { texte: designationPartie(sujet, payload), manquants: [] }
  } catch (e) {
    if (e instanceof IdentiteIncomplete) return { texte: null, manquants: e.manquants }
    throw e
  }
}

/** « Nom commercial, RCCM » : la liste des champs manquants en français. */
export function libelleManquants(cles: readonly string[]): string {
  return cles.map((c) => LIBELLE_CHAMP[c] ?? c).join(', ')
}

/** Pièces exigées d'un marchand selon son statut (même règle que `marchand_pieces_manquantes`). */
export function piecesRequisesMarchand(statut: StatutJuridique | null | undefined): string[] {
  switch (statut) {
    case 'entreprenant':
      return ['declaration_entreprenant', 'identite_gerant', 'photo_devanture']
    case 'personne_physique':
      return ['identite_gerant', 'photo_devanture']
    default:
      // Société, ou statut pas encore choisi (comptes d'avant 0050) : la règle d'origine.
      return ['rccm', 'nif', 'identite_gerant', 'photo_devanture']
  }
}
